import React, { useEffect, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { LinkedInInsight } from "./LinkedInInsight";
import { MetaPixel } from "./MetaPixel";

/**
 * Cookie consent for UK/EU visitors.
 * - "necessary" cookies always run (session, auth, forms).
 * - "marketing" covers the LinkedIn Insight Tag (advertising/analytics).
 * Choice is persisted to localStorage under `vemoye_cookie_consent_v1`:
 *   { marketing: boolean, decidedAt: ISO string }
 * If no decision yet -> banner shows and marketing scripts are NOT loaded.
 */

const KEY = "vemoye_cookie_consent_v1";
const CookieCtx = createContext({ marketing: false, decided: false, decide: () => {} });

export const useCookieConsent = () => useContext(CookieCtx);

const readConsent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.marketing === "boolean") return parsed;
    return null;
  } catch { return null; }
};

const writeConsent = (marketing) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ marketing, decidedAt: new Date().toISOString() }));
    window.dispatchEvent(new CustomEvent("vemoye:consent", { detail: { marketing } }));
  } catch {}
};

export const CookieConsentProvider = ({ children }) => {
  const [state, setState] = useState({ marketing: false, decided: false });

  useEffect(() => {
    const existing = readConsent();
    if (existing) setState({ marketing: !!existing.marketing, decided: true });
    const onChange = (e) => setState({ marketing: !!e.detail.marketing, decided: true });
    window.addEventListener("vemoye:consent", onChange);
    return () => window.removeEventListener("vemoye:consent", onChange);
  }, []);

  const decide = (marketing) => {
    writeConsent(marketing);
    setState({ marketing: !!marketing, decided: true });
  };

  return (
    <CookieCtx.Provider value={{ ...state, decide }}>
      {children}
    </CookieCtx.Provider>
  );
};

const CookieBanner = () => {
  const { decided, decide } = useCookieConsent();
  if (decided) return null;
  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-[100] rounded-lg shadow-[0_20px_50px_-10px_rgba(18,52,40,0.35)] max-w-[440px] p-5 md:p-6"
      style={{ background: "var(--cream)", border: "1px solid rgba(18,52,40,0.12)" }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="section-eyebrow" style={{ color: "var(--gold-hover)" }}>Cookies</div>
        <button
          onClick={() => decide(false)}
          aria-label="Reject analytics cookies"
          className="opacity-60 hover:opacity-100"
          style={{ color: "var(--forest)" }}
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-[14px] leading-relaxed mb-4" style={{ color: "rgba(18,52,40,0.85)" }}>
        We use essential cookies to run this site and, with your permission, marketing cookies
        (the LinkedIn Insight Tag and Meta Pixel) to help us reach more Nigerian diaspora professionals.
        Read our <Link to="/privacy" className="link-underline" style={{ color: "var(--gold-hover)" }}>Privacy &amp; Cookie Policy</Link>.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="cookie-accept-btn"
          onClick={() => decide(true)}
          className="inline-flex items-center px-4 py-2 rounded-md btn-gold text-[13px] font-medium"
        >
          Accept all
        </button>
        <button
          data-testid="cookie-reject-btn"
          onClick={() => decide(false)}
          className="inline-flex items-center px-4 py-2 rounded-md btn-outline text-[13px] font-medium"
        >
          Reject marketing
        </button>
      </div>
    </div>
  );
};

/**
 * Renders the cookie banner + loads LinkedIn Insight Tag when marketing
 * consent is granted. Use inside public flows only (Layout, PreLaunchShell,
 * PreLaunch). Do NOT render on admin/member pages.
 */
export const PublicConsent = () => {
  const { marketing } = useCookieConsent();
  return (
    <>
      <LinkedInInsight consent={marketing} />
      <MetaPixel consent={marketing} />
      <CookieBanner />
    </>
  );
};

export default PublicConsent;
